<template>
  <v-card flat class="pa-0">
    <v-row no-gutters class="mb-2" align="center" justify="space-between">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <p class="subtitle-2 font-weight-bold ma-0">
          {{ _getLabel('DIAGNOSES') }}
        </p>
        <v-row no-gutters class="ma-0" justify="end" align="center">
          <v-btn
            small
            outlined
            color="primary"
            class="text-none mr-2 clinical-diagnosis-action"
          :disabled="readonly"
          @click="addDiagnosis"
          >
            {{ _getLabel('ADD_DIAGNOSIS') }}
          </v-btn>
          <v-btn
            small
            outlined
            color="primary"
            class="text-none clinical-diagnosis-action"
          :disabled="readonly"
          @click="openDdx"
          >
            {{ _getLabel('ADD_FROM_DDX') }}
          </v-btn>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-if="!visibleDiagnoses.length" no-gutters>
      <v-col cols="12">
        <p class="caption grey--text text--darken-1 ma-0">
          {{ _getLabel('NO_DIAGNOSIS_ADDED') }}
        </p>
      </v-col>
    </v-row>

    <v-row v-for="diag in visibleDiagnoses" :key="`diag-${diag.index}`" no-gutters>
      <v-col cols="12" class="mb-3">
        <v-card flat class="vetalfa-card-grey-border pa-3">
          <v-row no-gutters align="center" justify="space-between">
            <v-col cols="11">
              <p class="subtitle-2 font-weight-bold ma-0">
                {{ _getLabel('DIAGNOSIS') }} {{ diag.index + 1 }}
              </p>
            </v-col>
            <v-col cols="1" class="text-right">
              <v-btn icon x-small :disabled="readonly" @click="removeDiagnosis(diag.index)">
                <v-icon size="16">{{ mdiTrashCanOutline }}</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <p v-if="readonly" class="clinical-diagnosis-value">{{ diag.item.name || '—' }}</p>
          <v-text-field
            v-else
            v-model="diag.item.name"
            outlined
            dense
            :label="_getLabel('DIAGNOSIS_NAME')"
          ></v-text-field>

          <v-row v-if="!readonly" no-gutters>
            <v-col :cols="_xsOnly() ? 12 : 6" :class="{ 'pr-2': !_xsOnly() }">
              <v-select
                v-model="diag.item.type"
                :items="diagnosisTypes"
                item-text="text"
                item-value="value"
                outlined
                dense
                :label="_getLabel('DIAGNOSIS_TYPE')"
              ></v-select>
            </v-col>
            <v-col :cols="_xsOnly() ? 12 : 6" :class="{ 'pl-2': !_xsOnly() }">
              <v-select
                v-model="diag.item.status"
                :items="diagnosisStatus"
                item-text="text"
                item-value="value"
                outlined
                dense
                :label="_getLabel('DIAGNOSIS_STATUS')"
              ></v-select>
            </v-col>
          </v-row>

          <p v-if="readonly" class="clinical-diagnosis-meta">{{ diagnosisTypeText(diag.item.type) }} • {{ diagnosisStatusText(diag.item.status) }}</p>
          <v-textarea
            v-if="!readonly"
            v-model="diag.item.notes"
            outlined
            dense
            :label="_getLabel('NOTES')"
            rows="2"
          ></v-textarea>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mdiTrashCanOutline } from "@mdi/js";

export default {
  name: "DiagnosisForm",
  props: {
    value: {
      type: Array,
      default: () => [],
      required: false,
    },
    encounterId: {
      type: String,
      default: null,
      required: false,
    },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      mdiTrashCanOutline,
      localModel: this.normalize(this.value),
    };
  },
  computed: {
    diagnosisTypes() {
      return [
        { value: 0, text: this._getLabel("DIAGNOSIS_DEFINITIVE") },
        { value: 1, text: this._getLabel("DIAGNOSIS_PRESUMPTIVE") },
        { value: 2, text: this._getLabel("DIAGNOSIS_DIFFERENTIAL") },
      ];
    },
    diagnosisStatus() {
      return [
        { value: 0, text: this._getLabel("DIAGNOSIS_ACTIVE") },
        { value: 1, text: this._getLabel("DIAGNOSIS_RESOLVED") },
      ];
    },
    visibleDiagnoses() {
      return this.localModel
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !item.deleted);
    },
  },
  methods: {
    normalize(list) {
      return (list || []).map((item) => ({
        id: item.id || null,
        name: item.name || "",
        type: typeof item.type === "number" ? item.type : 1,
        status: typeof item.status === "number" ? item.status : 0,
        notes: item.notes || "",
        deleted: item.deleted || null,
      }));
    },
    addDiagnosis() {
      // Sem `id` e sem `deleted`: os models do backend tratam apenas `undefined`
      // como campo ausente, entao mandar null aqui fazia o salvamento voltar 400.
      this.localModel.push({
        name: "",
        type: 1,
        status: 0,
        notes: "",
      });
    },
    removeDiagnosis(index) {
      const item = this.localModel[index];
      if (item && item.id) {
        this.$set(this.localModel, index, { ...item, deleted: Date.now() });
        return;
      }
      this.localModel.splice(index, 1);
    },
    openDdx() {
      if (!this.encounterId) return;
      this.$router.push({
        path: "/ddx",
        query: {
          encounterId: this.encounterId,
          returnTo: this.$route.fullPath,
        },
      });
    },
    diagnosisTypeText(value) {
      const diagnosisType = this.diagnosisTypes.find(
        (item) => item.value === value
      );
      return diagnosisType ? diagnosisType.text : "—";
    },
    diagnosisStatusText(value) {
      const status = this.diagnosisStatus.find((item) => item.value === value);
      return status ? status.text : "—";
    },
  },
  watch: {
    value: {
      handler(val) {
        const normalized = this.normalize(val);
        if (JSON.stringify(normalized) !== JSON.stringify(this.localModel)) {
          this.localModel = normalized;
        }
      },
      deep: true,
    },
    localModel: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.value)) {
          this.$emit("input", val);
        }
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.clinical-diagnosis-value { color:#193153; font-size:15px; margin:12px 0 4px; }
.clinical-diagnosis-meta { color:#667085; font-size:12px; margin:0 0 8px; }
.clinical-diagnosis-action { min-height:44px; }
</style>
